import { styled, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { IconProps } from "@phosphor-icons/react";
import { ArrowClockwiseIcon } from "@phosphor-icons/react/dist/csr/ArrowClockwise";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { InfoIcon as InfoPhosphorIcon } from "@phosphor-icons/react/dist/csr/Info";
import { MinusCircleIcon } from "@phosphor-icons/react/dist/csr/MinusCircle";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { WarningIcon as WarningPhosphorIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { memo } from "react";
import { toTitleCase } from "../../lib/Util";

const SpinWrapper = styled("span")({
  display: "inline-flex",
  animation: "spin 1s linear infinite",
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
});

const StatusIconContainer = styled("div")(({ theme }) => ({
  paddingRight: theme.spacing(0.25),
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
}));

/**
 * Core Basic Status Icons
 **/

type BaseIconProps = {
  iconType: keyof typeof CORE_ICON_FONTS;
  spin?: boolean;
  color?: string;
  large?: boolean;
  size?: string | number;
  testLabel?: string;
} & Omit<IconProps, "size">;

const BaseIcon: React.FC<BaseIconProps> = ({
  iconType,
  spin = false,
  color,
  large = false,
  size,
  testLabel = "base-icon",
  ...rest
}) => {
  const theme = useTheme();
  const IconComponent = CORE_ICON_FONTS[iconType];
  const paletteByName = theme.palette as unknown as Record<string, { main: string } | undefined>;
  const newColor = color && color in theme.palette ? paletteByName[color]?.main : paletteByName[iconType]?.main;
  size = size || (large ? "2em" : "1em");
  const newProps = { color: newColor, size };
  const icon = <IconComponent data-test-label={testLabel} {...newProps} {...rest} />;
  return spin ? <SpinWrapper>{icon}</SpinWrapper> : icon;
};

const CancelStatusIconSyled = styled(XIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: "18px",
}));

const CancelStatusIcon = () => {
  return (
    <Tooltip title="Cancelled" placement="left" arrow>
      <CancelStatusIconSyled data-testid={"cancelIcon"} />
    </Tooltip>
  );
};
const CORE_ICONS = {
  inactive: "inactive",
  working: "working",
  completed: "completed",
  info: "info",
  warning: "warning",
  error: "error",
  skipped: "skipped",
} as const;

type CoreIconType = keyof typeof CORE_ICONS;

// Status strings arrive from the API, so these are keyed by an arbitrary string.
const CORE_ICON_BY_NAME: Record<string, CoreIconType> = CORE_ICONS;

const CORE_ICON_FONTS = {
  inactive: MinusCircleIcon,
  working: ArrowClockwiseIcon,
  completed: CheckCircleIcon,
  info: InfoPhosphorIcon,
  warning: WarningPhosphorIcon,
  error: WarningPhosphorIcon,
  skipped: TrashIcon,
};

type IconType = React.FC<Omit<BaseIconProps, "iconType">>;

const InactiveIcon: IconType = (props) => <BaseIcon iconType={"inactive"} {...props} />;
const WorkingIcon: IconType = (props) => (
  <BaseIcon iconType={"working"} data-test-label="working-icon" {...props} spin={true} />
);
const CompletedIcon: IconType = (props) => <BaseIcon iconType={"completed"} {...props} />;
const InfoIcon: IconType = (props) => <BaseIcon iconType={"info"} {...props} />;
const WarningIcon: IconType = (props) => <BaseIcon iconType={"warning"} {...props} data-test-label="warning-icon" />;
const ErrorIcon: IconType = (props) => <BaseIcon iconType={"error"} {...props} testLabel="error-icon" />;
const SkippedIcon: IconType = (props) => <BaseIcon iconType={"skipped"} {...props} />;

const CoreIconAux = ({
  type,
  color,
  large = false,
  fontSize,
  width = undefined,
  height = undefined,
}: {
  type: keyof typeof CORE_ICONS;
  color?: string;
  large?: boolean;
  fontSize?: string | number;
  width?: string;
  height?: string;
}) => {
  let icon = <InactiveIcon large={large} color={color} fontSize={fontSize} style={{ width: width, height: height }} />;
  if (type === CORE_ICONS.working) {
    icon = <WorkingIcon large={large} color={color} fontSize={fontSize} style={{ width: width, height: height }} />;
  } else if (type === CORE_ICONS.completed) {
    icon = (
      <CompletedIcon
        large={large}
        color="accentSuccess30"
        fontSize={fontSize}
        style={{ width: width, height: height }}
      />
    );
  } else if (type === CORE_ICONS.info) {
    icon = <InfoIcon large={large} color="accentInfo30" fontSize={fontSize} style={{ width: width, height: height }} />;
  } else if (type === CORE_ICONS.warning) {
    icon = (
      <WarningIcon large={large} color="accentWarning30" fontSize={fontSize} style={{ width: width, height: height }} />
    );
  } else if (type === CORE_ICONS.error) {
    icon = (
      <ErrorIcon large={large} color="accentDanger30" fontSize={fontSize} style={{ width: width, height: height }} />
    );
  } else if (type === CORE_ICONS.skipped) {
    icon = <SkippedIcon large={large} color={color} fontSize={fontSize} style={{ width: width, height: height }} />;
  }
  return icon;
};

/**
 *  Constants
 **/

const DOWNLOAD_STATUS_TO_ICON: Record<string, CoreIconType> = {
  queued: CORE_ICONS.working,
  requested: CORE_ICONS.working,
  downloading: CORE_ICONS.working,
  completed: CORE_ICONS.completed,
  retrying: CORE_ICONS.warning,
  failed: CORE_ICONS.error,
  skipped: CORE_ICONS.skipped,
};

const LEGACY_STATUS_TO_ICON: Record<string, CoreIconType> = {
  SOURCING: CORE_ICONS.inactive,
  PENDING: CORE_ICONS.inactive,

  PROCESSING: CORE_ICONS.working,
  STARTED: CORE_ICONS.working,

  SKIPPED: CORE_ICONS.skipped,
  RETRIED: CORE_ICONS.warning,

  SINKING: CORE_ICONS.completed,
  STOPPED: CORE_ICONS.completed,

  FATAL: CORE_ICONS.error,
  WARNING: CORE_ICONS.warning,
  ABANDONED: CORE_ICONS.error,
  ERROR: CORE_ICONS.error,
};

const STATUS_TO_HUMAN_LABEL: Record<string, string> = {
  SINKING: "Uploading",
  SOURCING: "Downloading",
  STARTED: "Processing",
  STOPPED: "Completed",
  FATAL: "Error",
  skipped: "Skipped",
  succeeded: "Completed",
  failed: "Failed",
};

/**
 *  Status Icon extending off CoreIcon including tooltips, test-labels and padding.
 **/

const StyledStatusIcon = styled("div")<{ noPadding?: boolean }>`
  text-decoration: none;
  padding: ${(props) => (props.noPadding ? "0px" : "5px")};
  display: inline-block;
`;

const LegacyStatusIcon = (props: StatusIconProps) => {
  const iconType = props.status ? LEGACY_STATUS_TO_ICON[props.status] : undefined;
  return <StatusIcon iconType={iconType} {...props} />;
};

const DownloadPhaseStatusIcon = (props: StatusIconProps) => {
  const iconType = props.status ? DOWNLOAD_STATUS_TO_ICON[props.status] : undefined;
  return <StatusIcon iconType={iconType} {...props} />;
};

type StatusIconProps = {
  iconType?: CoreIconType;
  status?: string;
  large?: boolean;
  noPadding?: boolean;
  hideTooltip?: boolean;
  color?: string;
  width?: string;
  height?: string;
};

const StatusIconAux = ({
  iconType,
  status,
  large = false,
  noPadding = false,
  hideTooltip = false,
  color = undefined,
  width = undefined,
  height = undefined,
}: StatusIconProps) => {
  iconType = iconType ? iconType : CORE_ICON_BY_NAME[status as string];
  if (iconType) {
    const icon = (
      <div data-status={status} data-test-label="status-div" data-testid="status-div">
        <CoreIcon large={large} type={iconType} color={color} width={width} height={height} />
      </div>
    );

    if (hideTooltip) {
      return <StyledStatusIcon noPadding={noPadding}>{icon}</StyledStatusIcon>;
    } else {
      let toolTipText = status;
      const statusToTooltip = STATUS_TO_HUMAN_LABEL;
      if (statusToTooltip[status as string]) {
        toolTipText = statusToTooltip[status as string];
      }
      return (
        <Tooltip id="tooltip" title={toTitleCase(toolTipText ?? "")} placement="left" arrow>
          <StyledStatusIcon noPadding={noPadding} data-cy="tooltip-container-status-icon">
            {icon}
          </StyledStatusIcon>
        </Tooltip>
      );
    }
  } else {
    return <span>{status}</span>;
  }
};

const StatusIcon = memo(StatusIconAux);
const CoreIcon = memo(CoreIconAux);

export {
  CancelStatusIcon,
  CoreIcon,
  CORE_ICON_BY_NAME,
  DownloadPhaseStatusIcon,
  ErrorIcon,
  LegacyStatusIcon,
  StatusIcon,
  StatusIconContainer,
};
export type { CoreIconType };
